import { CustomModal, ProtectedImg } from "components";
import React from "react";
import { Button } from "reactstrap";
import apiService from "services/api";
import { useMutation, useQueryClient } from "react-query";
import { checkIfDataURIIsPdf, encodeImageFileAsURL } from "utils/fileToBase64";
import { FiTrash } from "react-icons/fi";
import { useNotificationContext } from "contexts";

export const AddHomeworkModal = ({ open, hide, lessonDetail, refetch }) => {
	const extendedHide = () => {
		hide();
	};

	const { createNotification } = useNotificationContext();
	const [homeworkImgDatum, setHomeworkImgDatum] = React.useState([]);
	const reset = React.useCallback(() => {
		setHomeworkImgDatum(
			lessonDetail?.students?.map(
				(studentLessonRecord) =>
					studentLessonRecord.lessonHomework?.imgPaths ?? [],
			),
		);
	}, [lessonDetail]);

	React.useEffect(() => {
		reset();
	}, [reset, open]);

	const queryClient = useQueryClient();

	const [edited, setEdited] = React.useState(false);

	const mutation = useMutation({
		mutationFn: (e) => {
			e.preventDefault();
			return Promise.all(
				homeworkImgDatum?.map((homeworkImgData, index) => {
					return apiService.lessonRecord.studentRecord.addHomework(
						lessonDetail?.students[index]?.id,
						{ homeworkImgData },
					);
				}),
			);
		},
		onSuccess: () => {
			setEdited(false);
			refetch();
			createNotification({
				type: "success",
				message: "Homework saved successfully",
			});
			queryClient.refetchQueries(["lessonRecord", lessonDetail?.id]);
		},
	});

	const removeHomework = React.useCallback(
		(studentIndex, homeworkIndex) => {
			setEdited(true);
			return setHomeworkImgDatum((prev) => {
				const newData = [...prev];
				newData[studentIndex] = newData[studentIndex].filter(
					(_, i) => i !== homeworkIndex,
				);
				return newData;
			});
		},
		[setHomeworkImgDatum],
	);

	const handleUpload = React.useCallback(
		async (e, studentIndex) => {
			const files = e.target.files;
			if (!files?.length) return;
			setEdited(true);
			const fileData = [];

			for (let i = 0; i < files.length; i++) {
				fileData.push(encodeImageFileAsURL(files[i]));
			}
			await Promise.all(fileData).then((b64s) => {
				setHomeworkImgDatum((prev) => {
					prev[studentIndex] = [...prev[studentIndex], ...b64s];
					return prev;
				});
			});
		},
		[setHomeworkImgDatum],
	);

	return (
		<form onSubmit={(e) => mutation.mutateAsync(e)}>
			<CustomModal
				show={open}
				hideModal={extendedHide}
				checkDirty
				isDirty={edited}
				zIndex={1000}
				title="Add Homework"
				buttons={
					<>
						<Button color="success" type="submit" disabled={mutation.isLoading}>
							Save
						</Button>
						<Button color="reset" onClick={() => extendedHide()} type="button">
							Cancel
						</Button>
					</>
				}
				triggerAlertWhenClose={true}
				triggerAlertMessageWhenClose="Are you sure you want to close this tab?"
			>
				<div>
					{lessonDetail?.students?.map((studentLessonRecord, studentIndex) => {
						return (
							<React.Fragment key={studentLessonRecord.id}>
								<div className="text-white ml-2 mb-2">
									Student: {studentLessonRecord.studentDetail?.firstName}{" "}
									{studentLessonRecord.studentDetail?.lastName}
								</div>
								<div className="d-flex justify-content-center flex-wrap">
									<input
										accept="image/*,application/pdf"
										className="form-control"
										onChange={(e) => handleUpload(e, studentIndex)}
										type="file"
										multiple
									/>
								</div>
								<div
									style={{ gap: 5 }}
									className="d-flex flex-wrap mt-2 align-items-start"
								>
									{homeworkImgDatum?.[studentIndex]?.map(
										(b64, homeworkIndex) => {
											const isDataUri = b64.startsWith("data");
											const isPdf = checkIfDataURIIsPdf(b64);
											return (
												<div
													key={b64.slice(-6)}
													className="d-flex position-relative align-items-start"
												>
													<ProtectedImg
														style={{ height: 200, objectFit: "cover" }}
														key={homeworkIndex}
														isDataUri={isDataUri}
														noToken={isDataUri}
														isPdf={isPdf}
														className="rounded"
														src={b64}
													/>
													<Button
														size="sm"
														className="btn-icon bg-danger btn-link edit my-0"
														style={
															isPdf
																? {}
																: {
																		position: "absolute",
																		top: 4,
																		right: 4,
																	}
														}
														onClick={() =>
															removeHomework(studentIndex, homeworkIndex)
														}
													>
														<FiTrash size={20} className="text-white" />
													</Button>
												</div>
											);
										},
									)}
								</div>
							</React.Fragment>
						);
					})}
				</div>
			</CustomModal>
		</form>
	);
};
